import { DateValue } from 'react-aria';

import { CalendarDate, getDayOfWeek, getLocalTimeZone } from '@internationalized/date';

import { preProcessDay } from '~/utilities/dates';

// Minimum valid year to prevent invalid dates, arbitrarily set to 1900.
const MIN_VALID_YEAR = 1900;

/**
 * @isWeekend
 * Whether a date falls on a Saturday or Sunday. Uses a fixed en-US week so the result matches
 * the business-day rules used elsewhere in the app rather than the locale's weekend.
 * */
export function isWeekend(date: DateValue) {
  const day = getDayOfWeek(date, 'en-US'); // 0 = Sunday, 6 = Saturday
  return day === 0 || day === 6;
}

/**
 * @toCalendarDate
 * Converts any number of date string formats (i.e "2024-02-14T08:00:00.000Z" or "2/14/2024") into a CalendarDate
 * should also turn dates at T00:00:00.000Z into that calendar date, not the previous day (in California)
 * */
export function toCalendarDate(dateString: string | null | undefined) {
  if (typeof dateString !== 'string') return dateString;
  const date = preProcessDay(new Date(dateString));
  const calendarDate = new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  if ([calendarDate.month, calendarDate.day, calendarDate.year].includes(NaN)) return null;
  return calendarDate;
}

/**
 * @toISODateString
 * Converts a CalendarDate into an ISOString (i.e "2024-02-14T08:00:00.000Z")
 * */
export function toISODateString(date: CalendarDate) {
  return date.toDate(getLocalTimeZone()).toISOString();
}

/**
 * @toLocaleDateString
 * Converts a CalendarDate into a LocaleDateString for display (i.e "2/14/2024")
 * */
export function toLocaleDateString(date: CalendarDate) {
  return date.toDate(getLocalTimeZone()).toLocaleDateString();
}

export function isValidDate(
  value: CalendarDate,
  minValue?: CalendarDate,
  maxValue?: CalendarDate,
  isDateUnavailable?: (date: DateValue) => boolean
) {
  if (value.year < MIN_VALID_YEAR) return false;
  if (minValue && value.compare(minValue) < 0) return false;
  if (maxValue && value.compare(maxValue) > 0) return false;
  if (isDateUnavailable?.(value)) return false;
  return true;
}

export function getErrorMessage(
  value: CalendarDate | null,
  minValue?: CalendarDate,
  maxValue?: CalendarDate,
  isDateUnavailable?: (date: DateValue) => boolean,
  unavailableMessage?: string
) {
  if (!value) return undefined;

  if (value.year < MIN_VALID_YEAR) return 'Please enter a valid date';
  if (maxValue && value.compare(maxValue) > 0)
    return `Date must be on or before ${toLocaleDateString(maxValue)}`;
  if (minValue && value.compare(minValue) < 0)
    return `Date must be on or after ${toLocaleDateString(minValue)}`;
  if (isDateUnavailable?.(value)) return unavailableMessage ?? 'Please select an available date';

  return undefined;
}

/**
 * Autofixes incomplete or ambiguous date segments.
 * - If year is missing, assumes next occurrence of month/day.
 * - If year < 100, assumes year is in 2000s.
 * Returns the fixed CalendarDate (or undefined if no fix applied), and optionally the fixed year.
 */
export function getAutofixedCalendarDate(
  year: number | undefined,
  month: number | undefined,
  day: number | undefined
): CalendarDate | undefined {
  if (!month || !day) return undefined; // cannot fix without month/day

  const currDate = new Date();
  const [currMonth, currDay, currYear] = [
    currDate.getMonth() + 1,
    currDate.getDate(),
    currDate.getFullYear(),
  ];

  // Case 1: Year missing, assume next occurrence of month/day
  if (!year) {
    if (currMonth > month || (currMonth === month && currDay > day))
      return new CalendarDate(currYear + 1, month, day);
    return new CalendarDate(currYear, month, day);
  }
  // Case 2: Year present but < 100, assume 2000s
  if (year && year < 100) {
    return new CalendarDate(year + 2000, month, day);
  }

  return undefined;
}
